import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { getCompanyWithClients, deleteCompany } from '../services/companies';
import { updateClient, getClients } from '../services/clients';
import './CompanyProfile.css';

export default function CompanyProfile() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [company, setCompany] = useState(null);
  const [clients, setClients] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showAddClientModal, setShowAddClientModal] = useState(false);
  const [searchingClients, setSearchingClients] = useState(false);
  const [clientSearchTerm, setClientSearchTerm] = useState('');
  const [clientSearchResults, setClientSearchResults] = useState([]);

  const loadCompany = useCallback(async () => {
    try {
      setLoading(true);
      const data = await getCompanyWithClients(id);
      setCompany(data);
      setClients(data?.clients || []);
    } catch (error) {
      console.error('Error loading company:', error);
      alert('Failed to load company: ' + error.message);
      navigate('/clients');
    } finally {
      setLoading(false);
    }
  }, [id, navigate]);

  useEffect(() => {
    loadCompany();
  }, [loadCompany]);

  const handleDelete = async () => {
    if (window.confirm('Are you sure you want to delete this company? All clients will be unlinked from this company.')) {
      try {
        await deleteCompany(id);
        navigate('/clients');
      } catch (error) {
        alert('Failed to delete company: ' + error.message);
      }
    }
  };

  const handleSearchClients = async (searchTerm) => {
    if (!searchTerm || searchTerm.trim() === '') {
      setClientSearchResults([]);
      return;
    }

    setSearchingClients(true);
    try {
      const allClients = await getClients();
      // Filter clients that are not linked to this company (or are null)
      const availableClients = allClients.filter(
        (client) =>
          (client.company_id === null || client.company_id !== id) &&
          client.name.toLowerCase().includes(searchTerm.toLowerCase())
      );
      setClientSearchResults(availableClients.slice(0, 10));
    } catch (error) {
      console.error('Error searching clients:', error);
      setClientSearchResults([]);
    } finally {
      setSearchingClients(false);
    }
  };

  const handleAddClient = async (client) => {
    if (!client) return;

    try {
      await updateClient(client.id, { company_id: id });
      await loadCompany();
      setShowAddClientModal(false);
      setClientSearchTerm('');
      setClientSearchResults([]);
    } catch (error) {
      alert('Failed to add client to company: ' + error.message);
    }
  };

  const handleRemoveClient = async (clientId) => {
    if (!window.confirm('Are you sure you want to remove this client from the company?')) {
      return;
    }

    try {
      await updateClient(clientId, { company_id: null });
      await loadCompany();
    } catch (error) {
      alert('Failed to remove client from company: ' + error.message);
    }
  };

  if (loading) {
    return (
      <div className="loading-container">
        <div className="loading-spinner"></div>
      </div>
    );
  }

  if (!company) {
    return (
      <div className="company-profile">
        <div className="error-message">Company not found</div>
      </div>
    );
  }

  return (
    <div className="company-profile">
      <div className="profile-header">
        <button className="btn-back" onClick={() => navigate('/clients')}>
          ← Back to Clients
        </button>
        <div className="header-actions">
          <button className="btn-delete-profile" onClick={handleDelete}>
            Delete Company
          </button>
        </div>
      </div>

      <div className="profile-content">
        <div className="company-name-header">
          <h1>{company.name}</h1>
        </div>

        <div className="company-info-card">
          <div className="info-grid">
            <div className="info-item">
              <span className="info-label">Company ID</span>
              <span className="info-value">{company.id}</span>
            </div>
            {company.created_at && (
              <div className="info-item">
                <span className="info-label">Created</span>
                <span className="info-value">
                  {new Date(company.created_at).toLocaleDateString()}
                </span>
              </div>
            )}
            <div className="info-item">
              <span className="info-label">Total Clients</span>
              <span className="info-value">{clients.length}</span>
            </div>
          </div>
        </div>

        <div className="clients-section">
          <div className="section-header">
            <h2>Clients in this Company</h2>
            <button className="btn-primary" onClick={() => setShowAddClientModal(true)}>
              + Add Client
            </button>
          </div>

          {clients.length === 0 ? (
            <div className="empty-state">
              <p>No clients linked to this company yet.</p>
            </div>
          ) : (
            <div className="clients-list">
              {clients.map((client) => (
                <div key={client.id} className="client-card">
                  <div className="client-info">
                    <h3>{client.name}</h3>
                    {client.email && <p className="client-email"><a href={`mailto:${client.email}`} className="email-link">{client.email}</a></p>}
                    {client.phone && <p className="client-phone"><a href={`tel:${client.phone}`} className="phone-link">{client.phone}</a></p>}
                  </div>
                  <div className="client-actions">
                    <button
                      className="btn-view"
                      onClick={() => navigate(`/clients/${client.id}`)}
                    >
                      View Client
                    </button>
                    <button
                      className="btn-remove"
                      onClick={() => handleRemoveClient(client.id)}
                    >
                      Remove
                    </button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>

      {/* Add Client Modal */}
      {showAddClientModal && (
        <div className="modal-overlay" onClick={() => setShowAddClientModal(false)}>
          <div className="site-address-modal" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h2>Add Client to Company</h2>
              <button className="btn-close" onClick={() => setShowAddClientModal(false)}>×</button>
            </div>
            <div className="site-address-modal-body">
              <div className="form-group">
                <label>Search for Client</label>
                <input
                  type="text"
                  className="search-input"
                  placeholder="Type client name to search..."
                  value={clientSearchTerm}
                  onChange={(e) => {
                    const term = e.target.value;
                    setClientSearchTerm(term);
                    handleSearchClients(term);
                  }}
                  autoFocus
                />
                {searchingClients && (
                  <div className="search-spinner" style={{ marginTop: '0.5rem' }}>Searching...</div>
                )}
                {clientSearchResults.length > 0 && (
                  <div className="search-results">
                    {clientSearchResults.map((client) => (
                      <div
                        key={client.id}
                        className="search-result-item"
                        onClick={() => handleAddClient(client)}
                      >
                        <div className="result-name">{client.name}</div>
                        {client.email && <div className="result-email" onClick={e => e.stopPropagation()}><a href={`mailto:${client.email}`} className="email-link">{client.email}</a></div>}
                      </div>
                    ))}
                  </div>
                )}
                {clientSearchTerm && !searchingClients && clientSearchResults.length === 0 && (
                  <div className="empty-results">No available clients found</div>
                )}
              </div>
            </div>
            <div className="site-address-modal-footer">
              <button
                type="button"
                className="btn-secondary"
                onClick={() => {
                  setShowAddClientModal(false);
                  setClientSearchTerm('');
                  setClientSearchResults([]);
                }}
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
